import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { formatDistanceToNow } from "date-fns";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";

interface OrderStatsResult {
  ordersLast90Days: number;
  projectedAnnualOrders: number;
  returnRate: number;
  fetchedAt: Date;
  unavailable: boolean;
}

const PERCENT_FORMAT = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});

export const OnboardingOrderCountCard = memo(function OnboardingOrderCountCard({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrderStatsResult | null>(null);

  const fetchStats = useHandler(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await rpcClient.getOnboardingOrderCount({ teamId }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  });

  return (
    <Flex dir="column" gap="md">
      <RedoButton
        disabled={loading}
        hierarchy="primary"
        onClick={fetchStats}
        size="sm"
        text={loading ? "Fetching…" : "Get Shopify onboarding stats"}
        theme="normal"
      />
      {error ? (
        <Text fontSize="sm" textColor="error">
          Failed to fetch: {error}
        </Text>
      ) : result?.unavailable ? (
        <Text fontSize="sm" textColor="secondary">
          Shopify analytics aren&apos;t available for this merchant&apos;s
          Shopify app (shopifyqlQuery / read_reports access denied).
        </Text>
      ) : result ? (
        <Flex gap="3xl">
          <Flex dir="column" gap="xs">
            <Text fontSize="2xl" fontWeight="semibold">
              {result.projectedAnnualOrders.toLocaleString()}
            </Text>
            <Text fontSize="sm" textColor="secondary">
              projected annual orders (
              {result.ordersLast90Days.toLocaleString()} in the last 90 days ×
              4)
            </Text>
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="2xl" fontWeight="semibold">
              {PERCENT_FORMAT.format(result.returnRate)}
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Shopify return rate — returns ÷ gross sales (last 90 days)
            </Text>
          </Flex>
        </Flex>
      ) : null}
      {result && !result.unavailable ? (
        <Text fontSize="xs" textColor="secondary">
          Fetched {formatDistanceToNow(result.fetchedAt, { addSuffix: true })} ·
          live from Shopify analytics
        </Text>
      ) : null}
    </Flex>
  );
});
